import { Badge } from "reactstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { OrderActionsCell } from "./OrderActionsCell";
import { getShortId } from "../../utils/getShortId";
import { getStatusColor, ORDER_STATUS_LABELS, PAYMENT_METHOD_LABELS, PAYMENT_PROVIDER_LABELS } from "../../utils/orderConstants";
import { formatDate } from "../../utils/formatDate";

export const PAYMENT_CHANNEL_CONFIG = {
  pse_payment: {
    label: "PSE",
  },
  card_payment: {
    label: "Tarjeta",
  },
  bank_transfer: {
    label: "Transferencia Bancolombia",
  },
  cash_efecty: {
    label: "Efecty",
  },
  nequi_payment: {
    label: "Nequi",
  },  
};

export const getOrderColumns = ({
  openPaymentProof
}) => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.display({
      id: "id",
      header: "ID",
      cell: (props) => {
        const id = props.row.original.id;
        return `${getShortId(id)}...` ;
      },
    }),

    columnHelper.display({
      id: "usuario",
      header: "Usuario",
      cell: (props) => {
        const user = props.row.original.user_id;
        return user ? (
          <>
            <div>{user.name}</div>
            <small className="text-muted">{user.email}</small>
          </>
        ) : (
          <span className="text-muted">Sin usuario</span>
        );
      },
    }),
    columnHelper.display({
      id: "destino",
      header: "Destino",
      cell: (props) => {
        const order = props.row.original;
        // Primero la dirección guardada en la orden (la que muestra el detalle):
        // `address_id` puede venir vacío o apuntar a una dirección borrada.
        const s = order.shipping_address?.address
          ? order.shipping_address
          : order.address_id || order.shipping;
        if (!s) {
          return (
            <span className="text-muted">
              {order.delivery_type === "DELIVERY" ? "Sin dirección" : "Recoger en tienda"}
            </span>
          );
        }

        // Snapshot: { country: { name } }; poblado: country_id.name; viejo: texto.
        const nameOf = (value) => (typeof value === "string" ? value : value?.name);
        const country = s.country_id?.name || nameOf(s.country);
        const department = s.province_id?.name || nameOf(s.province) || s.department;
        const city = s.city_id?.name || nameOf(s.city);

        return (
          <div>
            <strong>{country}</strong> - {department}, {city}
            <br />
            {s.address}
            {s.neighborhood && s.neighborhood !== "No aplica" && <div>Barrio: {s.neighborhood}</div>}
            {s.postal_code && <div>Código postal: {s.postal_code}</div>}
            {order.shipping_address_edits?.length > 0 && (
              <span className="badge bg-warning mt-1">Dirección editada</span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("items", {
      header: "Cant. productos",
      cell: (info) => {
        const items = info.getValue() || [];
        const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        return totalQuantity;
      },
    }),
    columnHelper.accessor("total", {
      header: "Total",
      cell: (info) => `$${info.getValue().toLocaleString()}`,
    }),
    columnHelper.display({
      id: "status",
      header: "Estado",
      cell: (props) => {
        const status = props.row.original.status;
        const color = getStatusColor(status);
        return (
          <>
            <Badge color={color}>{ORDER_STATUS_LABELS[status] || status}</Badge>
            {props.row.original.is_returned && (
              <Badge color="warning" className="ms-1">Devuelta</Badge>
            )}
          </>
        );
      },
    }),
    columnHelper.display({
      id: "is_paid",
      header: "Pagada",
      cell: (props) => {
        const order = props.row.original;
        const isPaid = order.is_paid;
        const status = order.status;

        if (status === "CANCELLED") {
          return <Badge color="danger">Cancelada</Badge>;
        }

        return isPaid ? (
          <Badge color="success">Sí</Badge>
        ) : (
          <Badge color="warning">No</Badge>
        );
      },
    }),
   columnHelper.display({
      id: "payment_method",
      header: "Método de pago",
      cell: (props) => {
        const order = props.row.original;
        const method = order.payment_method;
        const provider = order.payment_provider;
        const channel = order.payment_channel;

        const methodLabel = PAYMENT_METHOD_LABELS[method] || "No especificado";

        const isMonnet = provider === "MONNET";
        const providerLabel =
          method === "PROVIDER" && provider
            ? ` (${PAYMENT_PROVIDER_LABELS[provider] || provider}${isMonnet ? "" : ""})`
            : "";

        const channelLabel =
          isMonnet && channel
            ? ` - ${PAYMENT_CHANNEL_CONFIG[channel]?.label || channel}`
            : "";

        const isStorePickupTransfer = method === "TRANSFER";

        return (
          <div>
            <span>
              {methodLabel}
              {providerLabel}
              {channelLabel}
            </span>

            {isStorePickupTransfer && (
              <button
                type="button"
                onClick={() => {
                  if (typeof openPaymentProof === "function") openPaymentProof(order);
                }}
                className="ms-2 text-decoration-underline btn btn-link p-0 border-0"
                style={{ verticalAlign: "baseline" }}
              >
                Ver comprobante
              </button>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      header: "Fecha creación",
      cell: (info) => {
        const dateValue = info.getValue();
        if (!dateValue) return "-";
        try {
          return formatDate(dateValue);
        } catch (error) {
          return format(new Date(dateValue), "dd/MM/yyyy HH:mm");
        }
      },
    }),

    columnHelper.display({
      id: "acciones",
      header: "Acciones",
      cell: (props) => <OrderActionsCell order={props.row.original} />,
    })
  ];
};
